<template>
    <section class="external-schedule">
        <external-calendar
            :year="year"
            :month="month"
            :dates="dates"
            :physician="physician"
            :practice="practice"
        ></external-calendar>
    </section>
</template>

<script>
    import dayjs from 'dayjs'
    import ExternalCalendar from 'components/page-elements/referrals-new-schedule/ExternalCalendar'
    import { getEligibleDateRange, isDateEligible, isSlotEligible } from '@/utils/referralTimeframeUtils'

    export default {
        components: {
            ExternalCalendar,
        },

        props: {
            physician: { type: Object, required: true },
            practice: { type: Object, required: true },
            timeframe: { type: String, default: null },
            createdAt: { type: String, default: null },
            currentDate: { type: Date, default: () => new Date() },
        },

        provide() {
            return {
                referralTimeframe: this.timeframe,
                referralCreatedAt: this.createdAt,
            }
        },

        data() {
            return {
                dates: {},
                year: dayjs().year(),
                month: dayjs().month(),
            }
        },

        methods: {
            setInitialMonth() {
                if (this.$route.query.date) {
                    const date = dayjs(this.$route.query.date)
                    this.year = date.year()
                    this.month = date.month()
                    return
                }

                let focusDate = dayjs(this.currentDate).startOf('day')

                if (this.timeframe && this.createdAt) {
                    const created = dayjs(this.createdAt)

                    if (created.isValid()) {
                        const eligibleRange = getEligibleDateRange(this.timeframe, created)
                        focusDate = eligibleRange.start.startOf('day')
                        const today = dayjs().startOf('day')

                        if (focusDate.isBefore(today)) {
                            focusDate = today
                        }

                        if (focusDate.isAfter(eligibleRange.end, 'day')) {
                            focusDate = dayjs(this.currentDate).startOf('day')
                        }
                    }
                }

                this.year = focusDate.year()
                this.month = focusDate.month()

                this.$router
                    .replace({
                        query: {
                            ...this.$route.query,
                            date: focusDate.format('YYYY-MM-DD'),
                        },
                    })
                    .catch(() => {})
            },

            generateSlotsForDay(currentDate) {
                const today = dayjs().startOf('day')
                const created = this.createdAt ? dayjs(this.createdAt) : null
                const hasTimeframeFilter = this.timeframe && created?.isValid()

                if (!hasTimeframeFilter && !currentDate.isAfter(today, 'day')) {
                    return []
                }

                if (hasTimeframeFilter && !isDateEligible(currentDate, this.timeframe, created)) {
                    return []
                }

                const appointmentLength = this.physician.appointmentLength || 60
                const dayOfWeek = currentDate.format('dddd').toLowerCase()
                const workingHours = this.physician.workingHours?.[dayOfWeek]

                if (!workingHours || !workingHours.isWorking) {
                    return []
                }

                const [startHour, startMinute] = workingHours.start.split(':').map(Number)
                const [endHour, endMinute] = workingHours.end.split(':').map(Number)
                const startTimeInMinutes = startHour * 60 + startMinute
                const endTimeInMinutes = endHour * 60 + endMinute
                const slots = []

                for (let time = startTimeInMinutes; time < endTimeInMinutes; time += appointmentLength) {
                    const endTime = time + appointmentLength
                    if (endTime > endTimeInMinutes) break

                    const startHours = Math.floor(time / 60)
                    const startMins = time % 60
                    const endHours = Math.floor(endTime / 60)
                    const endMins = endTime % 60
                    const startTime = `${String(startHours).padStart(2, '0')}:${String(startMins).padStart(2, '0')}`
                    const endTimeLabel = `${String(endHours).padStart(2, '0')}:${String(endMins).padStart(2, '0')}`
                    const slotDateTime = dayjs(`${currentDate.format('YYYY-MM-DD')} ${startTime}`)

                    if (!slotDateTime.isAfter(dayjs())) {
                        continue
                    }

                    if (hasTimeframeFilter && !isSlotEligible(slotDateTime, this.timeframe, created)) {
                        continue
                    }

                    slots.push({
                        date: currentDate.format('YYYY-MM-DD'),
                        startTime,
                        endTime: endTimeLabel,
                        from: time,
                        to: endTime,
                    })
                }

                return slots
            },

            generateAvailableDates() {
                const dates = {}
                const startOfMonth = dayjs().year(this.year).month(this.month).startOf('month')
                const daysInMonth = startOfMonth.daysInMonth()

                for (let day = 1; day <= daysInMonth; day++) {
                    const currentDate = startOfMonth.date(day)
                    const slots = this.generateSlotsForDay(currentDate)

                    if (slots.length) {
                        dates[currentDate.startOf('day').format()] = slots
                    }
                }

                this.dates = dates
            },
        },

        watch: {
            '$route.query'(newQuery, oldQuery) {
                const dateChanged = newQuery.date !== oldQuery.date
                const viewChanged = newQuery.view !== oldQuery.view

                if (!dateChanged && !viewChanged) {
                    return
                }

                if (dateChanged) {
                    const date = newQuery.date ? dayjs(newQuery.date) : dayjs()
                    this.year = date.year()
                    this.month = date.month()
                }

                this.generateAvailableDates()
            },

            physician: {
                deep: true,
                handler() {
                    this.generateAvailableDates()
                },
            },
        },

        mounted() {
            this.setInitialMonth()
            this.generateAvailableDates()
        },
    }
</script>
